/// <reference path="../types.js" />
import { checkBranch } from "./checkBranch";

/**
 * Encontra o nó de término (join) de um branch a partir do nó de início do branch.
 * Considera ramos aninhados garantindo que o join retornado corresponda ao nível atual.
 * @param {Elements} elements
 * @param {Node|Edge} start
 * @returns {Node|Edge|null}
 */
function findBranchJoinNode(elements, start) {
  const startType = start?.type;
  const targetEndType =
    startType === "parallel"
      ? "parallelEnd"
      : startType === "conditional"
      ? "conditionalEnd"
      : null;

  if (!targetEndType) return null;

  const elementsById = new Map(elements.map((el) => [el.id, el]));
  const visited = new Set();
  const queue = [];

  elements.forEach((edge) => {
    if (edge.source === start.id) {
      const targetNode = elementsById.get(edge.target);
      if (targetNode) queue.push({ node: targetNode, depth: 0 });
    }
  });

  while (queue.length) {
    const { node, depth } = queue.shift();
    if (!node) continue;

    const visitKey = `${node.id}:${depth}`;
    if (visited.has(visitKey)) continue;
    visited.add(visitKey);

    let nextDepth = depth;

    if (node.type === startType) {
      nextDepth += 1;
    }

    if (node.type === targetEndType) {
      if (nextDepth === 0) {
        return node;
      }
      nextDepth = Math.max(nextDepth - 1, 0);
    }

    elements.forEach((edge) => {
      if (edge.source === node.id) {
        const targetNode = elementsById.get(edge.target);
        if (targetNode) {
          queue.push({ node: targetNode, depth: nextDepth });
        }
      }
    });
  }

  return null;
}

/**
 * Conta os ramos de um nó paralelo ou condicional, retornando o maior tempo e estatísticas.
 * @param {Elements} elements
 * @param {Node|Edge} item
 * @returns {Object}
 */
export function branchCount(elements, item) {
  let maxDone = 0;
  let maxPreventDone = 0;
  let task;
  let bestBranchStats = [];

  const allBranches = elements.filter((e) => e.source === item.id);
  const branchResults = allBranches.map((branch) => {
    const result = checkBranch(elements, branch);
    const weightSum = (result.stats || []).reduce(
      (acc, stat) => acc + (stat.weight || 0),
      0
    );

    return { result, weightSum };
  });

  if (!branchResults.length) {
    return {
      max: 0,
      lastItem: findBranchJoinNode(elements, item) || null,
      maxDone: 0,
      maxPreventDone: 0,
      stats: [],
      task: undefined,
    };
  }

  branchResults.forEach(({ result }) => {
    maxDone = Math.max(result.doneTime, maxDone);
    maxPreventDone = Math.max(result.donePreventTime, maxPreventDone);
  });

  const hasWeightPriority = branchResults.some(
    ({ weightSum }) => weightSum > 0
  );

  let selected = null;

  if (hasWeightPriority) {
    branchResults.forEach((entry) => {
      const { result, weightSum } = entry;
      const currentBestWeight = selected ? selected.weightSum : -Infinity;
      const currentBestTime = selected ? selected.result.branchTime : -Infinity;

      if (
        !selected ||
        weightSum > currentBestWeight ||
        (weightSum === currentBestWeight &&
          result.branchTime >= currentBestTime)
      ) {
        selected = entry;
      }
    });
  } else {
    let maxBranchTime = -Infinity;

    branchResults.forEach((entry) => {
      const { result } = entry;
      if (result.branchTime >= maxBranchTime) {
        maxBranchTime = result.branchTime;
        selected = entry;
      }
    });
  }

  if (selected) {
    task = selected.result.task;
    bestBranchStats = selected.result.stats || [];
  }

  const bestResult = selected?.result;
  const branchLastItem = bestResult?.lastItem;

  return {
    max: bestResult?.branchTime || 0,
    lastItem: branchLastItem || findBranchJoinNode(elements, item) || null,
    maxDone,
    maxPreventDone,
    stats: bestBranchStats,
    task,
  };
}
